import { useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import { Button } from '@/components/common/Button';
import { CpConfermaAbbandono } from '@/components/chinesepoker/CpConfermaAbbandono';
import { CpImpostazioniFoglio, ImpostazioniContenuto } from '@/components/chinesepoker/CpImpostazioni';
import { CpResoconto } from '@/components/chinesepoker/CpResoconto';
import { CpAzioni, CpEtichettaPlancia, CpFascia, CpTavoloResponsivo, CpTestata, CpTurno } from '@/components/chinesepoker/CpTavolo';
import type { ModoTavolo, PezziTavolo } from '@/components/chinesepoker/CpTavolo';
import { OfcBoard } from '@/components/chinesepoker/OfcBoard';
import { DrawTray } from '@/components/chinesepoker/DrawTray';
import type { TrascinaMano } from '@/components/chinesepoker/trascinaCp';
import { TastoOrdina } from '@/components/tavolo/TastoOrdina';
import { useActiveQcpMatch } from '@/contexts/ActiveMatchContext';
import { useEffettiCp, useVoloCarte } from '@/hooks/useEffettiCp';
import { useImpostazioniCp } from '@/hooks/useImpostazioniCp';
import { useOfcPlacement } from '@/hooks/useOfcPlacement';
import { useAppDispatch, useAppSelector } from '@/store/hooks';
import { setActiveMatch } from '@/store/activeMatch.slice';
import { cn } from '@/lib/cn';
import { resocontoDi } from '@/lib/qcp/resoconto';
import type { Card, Row, Seat } from '@/lib/qcp/protocol';
import { altroOrdine } from '@/lib/tavolo/ordinaMano';
import type { Ordine } from '@/lib/tavolo/ordinaMano';
import { useVibrazione } from '@/lib/tavolo/useVibrazione';

/** Oltre queste carte in mano (la Fantasyland: 14-17) il palco dispone la mano in una striscia larga sotto il tavolo, non nella colonna da 184 px. */
const SOGLIA_MANO_LARGA = 5;

/**
 * Live Chinese Poker (Pineapple OFC) match screen.
 *
 * Composes:
 *  - useActiveQcpMatch — reads the match socket from ActiveMatchProvider
 *    (the socket lives above the route tree, not per-screen)
 *  - useOfcPlacement — local pending-placement state for one round
 *  - OfcBoard / DrawTray — OFC UI components
 *  - CpTavoloResponsivo — la DISPOSIZIONE (fase 2, nello stile del Burraco «premium» di #718): in piedi una colonna, in orizzontale il palco 844×390 nel tabellone
 *    del modulo comune; i tasti sono gli stessi nei due modi, i pezzi si ricostruiscono per modo (le carte hanno taglie diverse)
 *  - CpResoconto — a `hand:settled` il resoconto della mano, dal `breakdown` del server (foul, scoop, royalty), al posto del tavolo
 *  - CpImpostazioniFoglio / ImpostazioniContenuto — «Quattro colori» e «Vibrazione», due preferenze del SOLO Chinese Poker (`useImpostazioniCp`)
 *
 * Layout in piedi (max-w-[520px]):
 *  Top    — testata (mano, round, punteggi) + opponent board (compact, placed cards only — anti-leak) + draw/discard counts
 *  Middle — clock pills, turn indicator
 *  Bottom — my board + draw tray, Conferma, Abbandona, impostazioni
 *
 * Anti-leak: opponent draw cards (private state) are NEVER rendered; only
 * `drawCount` and `discardCount` from the masked opponents projection are shown.
 */
export function ChinesePokerMatch() {
  const { id = '' } = useParams<{ id: string }>();
  const location = useLocation();
  const navigate = useNavigate();

  const { t } = useTranslation('chinesepoker');

  const active = useAppSelector((s) => s.activeMatch.current);
  const dispatch = useAppDispatch();

  // Fallback deep-link/reload: if we land on the match route without an
  // activeMatch for this id (e.g. WebView reload), register it from
  // location.state/:id so the provider connects. Mount-only bootstrap — it
  // must NOT react to later active.matchId changes, or the provider's
  // central teardown clearing the slice on natural match end would re-fire
  // this effect and resurrect the just-ended match.
  const bootstrappedRef = useRef(false);
  useEffect(() => {
    if (bootstrappedRef.current) return;
    bootstrappedRef.current = true;
    if (id && active?.matchId !== id) {
      const vsBot = (location.state as { vsBot?: boolean } | null)?.vsBot;
      dispatch(setActiveMatch({ game: 'qcp', matchId: id, vsBot, startedAt: Date.now() }));
    }
    // Bootstrap once on cold mount only — deps intentionally empty.
  }, []);

  const m = useActiveQcpMatch();

  // ── Le preferenze del tavolo (SOLO del Chinese Poker) e la vibrazione ────
  const imp = useImpostazioniCp();
  const vib = useVibrazione(imp.vibrazione);
  const vibra = useMemo(() => ({ impact: vib.impact, notification: vib.notification }), [vib.impact, vib.notification]);
  const [impostazioniAperte, setImpostazioniAperte] = useState(false);
  // «Abbandona» apre una conferma: `resign` parte solo da li, e una volta sola (`abbandonoInviato`).
  const [confermaAbbandono, setConfermaAbbandono] = useState(false);
  const abbandonoInviato = useRef(false);
  const abbandona = () => {
    setConfermaAbbandono(false);
    if (abbandonoInviato.current) return;
    abbandonoInviato.current = true;
    vibra.impact('medium');
    m.resign();
  };
  const [modo, setModo] = useState<ModoTavolo>('verticale');

  // ── Seat + Fantasyland identity (drives the asymmetric FL flow) ─────────
  const mySeat = m.mySeat;
  const oppSeat: Seat | null = mySeat === 'A' ? 'B' : mySeat === 'B' ? 'A' : null;
  // This seat in FL this hand → it sets all 13 at once from a 14-17 card draw.
  const myFlCount = mySeat ? m.flCardCount[mySeat] : 0;
  const myFl = mySeat ? m.inFantasyland[mySeat] : false;
  const oppFl = oppSeat ? m.inFantasyland[oppSeat] : false;

  // ── Placement state (local, per round / one-shot in FL) ────────────────
  const round = m.state?.public.round ?? 0;
  const placement = useOfcPlacement(round, m.draw, myFl ? myFlCount : 0);

  // A pending card "picked up" on the board (key `${r}${s}`) to move/remove it
  // this hand; null = nothing picked. Reset on round change / confirm / tray-pick.
  const [pickedBoardKey, setPickedBoardKey] = useState<string | null>(null);
  // Il trascinamento di una carta della mano (I-798, fase B): la fila che il dito ha sotto, e la carta che era scelta PRIMA del gesto (per rimettere le cose com'erano).
  const [filaSopra, setFilaSopra] = useState<Row | null>(null);
  const selezionePrima = useRef<number | null>(null);
  // L'ordine della mano (il tasto «Ordina», come il Burraco: parte per seme, non si ricorda): vale per la mano di tutte le mani, Fantasyland compresa.
  const [ordine, setOrdine] = useState<Ordine>('seme');
  // Per-row display order override (card keys). Lets the player rearrange cards
  // within a row — INCLUDING previously-placed (committed) cards. Purely cosmetic:
  // a row is scored as a set, so display order never affects the hand or what's
  // submitted. Reset each round.
  const [rowOrder, setRowOrder] = useState<Record<Row, string[]>>({ front: [], middle: [], back: [] });
  useEffect(() => {
    setPickedBoardKey(null);
    setRowOrder({ front: [], middle: [], back: [] });
  }, [round]);

  // ── Clock: 1 s tick decrementing the on-move seat's banked time ──────
  const [displayClocks, setDisplayClocks] = useState<Record<Seat, number>>({ A: 0, B: 0 });
  const clockOwnerRef = useRef<Seat | null>(null);
  const lastTickRef = useRef<number>(Date.now());

  useEffect(() => {
    if (!m.clocks) return;
    // Sync from server push. The server banks time in MILLISECONDS
    // (ClockManager.bankMs); the display + the 1 s tick below work in SECONDS,
    // so convert here — otherwise the clock reads e.g. "1939:11" (ms shown as s).
    setDisplayClocks({ A: m.clocks.banked.A / 1000, B: m.clocks.banked.B / 1000 });
    clockOwnerRef.current = m.clocks.owner;
    lastTickRef.current = Date.now();
  }, [m.clocks]);

  useEffect(() => {
    const interval = window.setInterval(() => {
      const owner = clockOwnerRef.current;
      if (!owner) return;
      const now = Date.now();
      const elapsed = (now - lastTickRef.current) / 1000;
      lastTickRef.current = now;
      setDisplayClocks((prev) => ({
        ...prev,
        [owner]: Math.max(0, prev[owner] - elapsed),
      }));
    }, 1000);
    return () => window.clearInterval(interval);
  }, []);

  // ── Navigate to result screen once the SET (multi-hand match) ends ──────
  // The terminal signal is match:result (the multi-hand "set" end), NOT the
  // legacy per-hand match:ended. ChinesePokerResult consumes { matchResult,
  // mySeat } from router state.
  // ⚠️ `match:result` dell'ULTIMA mano arriva nello stesso istante di `hand:settled`: la schermata Result resta DIETRO il resoconto di quella mano finche chi gioca
  // non lo chiude («Vedi il risultato»). Senza un resoconto aperto (resa, abbandono, nessuna mano chiusa) si va subito, come sempre.
  // ⚠️ Il hook sta nel provider, sopra la rotta: alla SECONDA partita di fila il primo render porta ancora il `matchResult` della PRIMA (l'effetto del hook lo azzera dopo, e gli effetti
  // dei figli girano prima di quelli del padre). Si naviga solo se il risultato e' di QUESTA partita: il payload porta `matchId`.
  useEffect(() => {
    if (m.matchResult && m.matchResult.matchId === id && id && !m.resocontoAperto) {
      navigate(`/chinesepoker/result/${id}`, {
        state: { matchResult: m.matchResult, mySeat: m.mySeat },
        replace: true,
      });
    }
  }, [m.matchResult, m.resocontoAperto, id, navigate, m.mySeat]);

  // ── Guard: wait for first state + seat identity ─────────────────────────
  const ready = mySeat !== null && m.state !== null;

  // ── Placement guard ────────────────────────────────────────────────────
  const isMyTurn = ready && m.state!.public.turn === mySeat;
  const isPlacingPhase = ready && m.state!.public.phase === 'placing';
  // Cards already committed to MY board (server-authoritative count).
  const myBoardPlaced =
    ready && mySeat
      ? m.state!.public.boards[mySeat].front.length +
        m.state!.public.boards[mySeat].middle.length +
        m.state!.public.boards[mySeat].back.length
      : 0;
  // FL: I set all 13 at once (not turn-gated) until my board is complete.
  // Normal: place on my turn during the placing phase.
  const showPlaceUI = isPlacingPhase && (myFl ? myBoardPlaced < 13 : isMyTurn);

  // ── Il resoconto della mano ─────────────────────────────────────────────
  // `hand:settled` porta il resoconto della mano appena chiusa (`breakdown`: foul, scoop, royalty). Resta a schermo finche l'hook lo dice aperto (`resocontoAperto`):
  // fino a «Prossima mano» (o «Vedi il risultato» a partita finita) o allo scadere dei 15 s. ⚠️ NON si toglie con `hand:start`: il server lo manda nello stesso
  // istante di `hand:settled` (staging 17/10), e il resoconto che si toglieva li non aveva mai un frame. «Prossima mano» manda il seed della mano dopo (l'hook lo tiene
  // finche il resoconto e aperto: il server aspetta il seed di tutti e due i posti e solo dopo distribuisce e fa partire gli orologi).
  const settled = m.handSettled ?? null;
  const resoconto = useMemo(() => (settled && mySeat && m.resocontoAperto ? resocontoDi(settled, mySeat) : null), [settled, mySeat, m.resocontoAperto]);
  // Il conto alla rovescia nel tasto: i secondi che mancano al seed automatico (l'hook ha l'ora, qui si ridisegna ogni secondo).
  const entro = m.prossimaManoEntro;
  const [adesso, setAdesso] = useState(() => Date.now());
  useEffect(() => {
    if (entro === null || entro === undefined) return;
    setAdesso(Date.now());
    const i = window.setInterval(() => setAdesso(Date.now()), 1000);
    return () => window.clearInterval(i);
  }, [entro]);
  const secondiAllaProssima = !m.matchResult && entro !== null && entro !== undefined ? Math.max(0, Math.ceil((entro - adesso) / 1000)) : null;

  // ── Effetti: volo delle carte, anello alla fila completata, «Tocca a te», «+N» (solo presentazione) ──
  const mieFile = ready && mySeat ? myFileConteggio(m.state!.public.boards[mySeat], placement.pending) : null;
  useVoloCarte(modo);
  useEffettiCp({
    mieFile,
    miaMossa: isMyTurn,
    resoconto,
    testi: { toccaATe: t('match.yourTurn'), fantasyland: t('hand.youInFantasyland') },
    vibra,
  });
  // L'errore del server (mossa rifiutata): una vibrazione di errore, una volta per errore.
  const erroreVisto = useRef<unknown>(null);
  useEffect(() => {
    if (m.error && m.error !== erroreVisto.current) vibra.notification('error');
    erroreVisto.current = m.error;
  }, [m.error, vibra]);
  // Gli ultimi secondi del MIO orologio, nel mio turno: un avviso, una volta per turno.
  const avvisoSecondiRef = useRef(false);
  const mieiSecondi = mySeat ? displayClocks[mySeat] : 0;
  useEffect(() => {
    if (!isMyTurn) {
      avvisoSecondiRef.current = false;
      return;
    }
    if (mieiSecondi > 0 && mieiSecondi <= 10 && !avvisoSecondiRef.current) {
      avvisoSecondiRef.current = true;
      vibra.notification('warning');
    }
  }, [isMyTurn, mieiSecondi, vibra]);

  // ── Confirm handler ────────────────────────────────────────────────────
  const handleConfirm = () => {
    if (!placement.isComplete) return;
    vibra.impact('medium');
    if (myFl) {
      // Fantasyland: submit the whole grouped board (3/5/5) + the leftover
      // discards (the 1-4 cards still in the draw) in a single place:fl move.
      const flBoard = {
        front: placement.pending.filter((p) => p.row === 'front').map((p) => p.card),
        middle: placement.pending.filter((p) => p.row === 'middle').map((p) => p.card),
        back: placement.pending.filter((p) => p.row === 'back').map((p) => p.card),
      };
      const placedSet = new Set(placement.pending.map((p) => `${p.card.r}${p.card.s}`));
      const flDiscards = m.draw.filter((c) => !placedSet.has(`${c.r}${c.s}`));
      m.placeFl(flBoard, flDiscards);
    } else {
      m.place(placement.pending.map(({ card, row }) => ({ card, row })), placement.discard);
    }
    placement.clear();
    setPickedBoardKey(null);
  };

  // ── Clock formatter: m:ss ─────────────────────────────────────────────
  function formatClock(seconds: number): string {
    const s = Math.max(0, Math.floor(seconds));
    const m = Math.floor(s / 60);
    const rem = s % 60;
    return `${m}:${rem.toString().padStart(2, '0')}`;
  }

  // ── Opponent draw/discard counts (anti-leak: no cards, only counts) ───
  const oppCounts = oppSeat ? m.state?.opponents[oppSeat] : undefined;

  // ── Empty board for loading state ─────────────────────────────────────
  const emptyBoard = { front: [], middle: [], back: [] };

  // ──────────────────────────────────────────────────────────────────────
  // RENDER
  // ──────────────────────────────────────────────────────────────────────

  // Connecting / seat-discovery loader
  if (!ready) {
    return (
      <div
        className="relative mx-auto flex max-w-[520px] flex-col items-center justify-center gap-3 px-3 py-12"
        style={{ paddingTop: 'calc(var(--tg-viewport-safe-area-inset-top, 0px) + 0.75rem)' }}
      >
        {m.connFail ? (
          <>
            <p className="rounded-xl bg-danger/15 px-4 py-2 text-sm text-danger">
              {t(m.connFail === 'non-disponibile' ? 'match.unavailable' : 'match.connFail')}
            </p>
            <Button variant="secondary" onClick={m.retry}>
              {t('match.retry')}
            </Button>
          </>
        ) : (
          <>
            <span className="h-3 w-3 animate-ping rounded-full bg-brand" />
            <p className="text-sm font-medium text-fg-muted">
              {m.connected ? t('match.settingUp') : t('match.connecting')}
            </p>
          </>
        )}
        {m.error && (
          <p className="rounded-xl bg-danger/15 px-4 py-2 text-sm text-danger">
            {m.error.message}
          </p>
        )}
      </div>
    );
  }

  const publicState = m.state!.public;

  // ── Optimistic placement ────────────────────────────────────────────────
  // Merge un-submitted pending placements into MY board so a card visibly moves
  // to its row the instant you tap (not only after Confirm), and drop placed
  // cards out of the draw tray. The placement hook indexes into the ORIGINAL
  // m.draw, so we tag each remaining card with its draw index and map tray
  // position → draw index when wiring the tray below.
  const cardKey = (c: { r: number; s: string }) => `${c.r}${c.s}`;
  const serverBoard = publicState.boards[mySeat];
  // Real cards in a row = committed (server) + this hand's pending placements.
  const rawRow = (row: Row): Card[] => [
    ...serverBoard[row],
    ...placement.pending.filter((p) => p.row === row).map((p) => p.card),
  ];
  // Apply the per-row display-order override: cards named in `order` first (in
  // that order), then any not listed (e.g. just-placed) appended.
  const orderRow = (cards: Card[], order: string[]): Card[] => {
    if (order.length === 0) return cards;
    const byKey = new Map(cards.map((c) => [cardKey(c), c] as const));
    const ordered: Card[] = [];
    for (const k of order) {
      const c = byKey.get(k);
      if (c) { ordered.push(c); byKey.delete(k); }
    }
    for (const c of cards) if (byKey.has(cardKey(c))) ordered.push(c);
    return ordered;
  };
  const myDisplayBoard = {
    front: orderRow(rawRow('front'), rowOrder.front),
    middle: orderRow(rawRow('middle'), rowOrder.middle),
    back: orderRow(rawRow('back'), rowOrder.back),
  };
  const placedKeys = new Set(placement.pending.map((p) => cardKey(p.card)));
  const remainingDraw = m.draw
    .map((card, idx) => ({ card, idx }))
    .filter(({ card }) => !placedKeys.has(cardKey(card)));
  /** Nel palco la mano della Fantasyland sta in una striscia sotto il tavolo (la colonna di destra, 184 px, non le tiene su una fila). */
  const manoLarga = (palco: boolean) => palco && remainingDraw.length > SOGLIA_MANO_LARGA;

  /**
   * La carta SCELTA va su `row`: l'UNICA piazzata, per il tocco su una fila e per il rilascio di un trascinamento (I-798, fase B). Il trascinamento non ha un percorso suo: prende la carta
   * (la sceglie, come il tocco) e al rilascio chiama questa, quindi quota, capienza e Fantasyland le decide `placement.placeTo`, come sempre.
   */
  const piazzaSuFila = (row: Row) => {
    vibra.impact('light');
    placement.placeTo(row);
    setPickedBoardKey(null);
  };

  /** I tocchi sulla MIA plancia (le regole dei tocchi non cambiano col restyle: stesse di prima). */
  const onSlotTap = (row: Row, index: number) => {
    const tapped = myDisplayBoard[row][index];
    const tappedKey = tapped ? cardKey(tapped) : null;
    const tappedPending = tappedKey ? placedKeys.has(tappedKey) : false;

    // 1) A draw card is selected in the tray → place it on this row.
    if (placement.selectedIndex !== null) {
      piazzaSuFila(row);
      return;
    }

    // 2) A board card is picked up → act relative to it.
    if (pickedBoardKey) {
      const picked = placement.pending.find((p) => cardKey(p.card) === pickedBoardKey);
      if (!picked) { setPickedBoardKey(null); return; }

      // 2a) Re-tap the SAME picked card → send it back to the tray.
      if (tappedKey === pickedBoardKey) {
        vibra.impact('light');
        placement.unplace(picked.card);
        setPickedBoardKey(null);
        return;
      }

      // Swap / move only WITHIN the picked card's row (cosmetic reorder).
      if (picked.row === row) {
        const current = rowOrder[row].length ? rowOrder[row] : myDisplayBoard[row].map(cardKey);
        if (tapped && tappedKey) {
          // 2b) Tap ANY other card in the row (committed OR pending) → swap.
          const next = [...current];
          const i = next.indexOf(pickedBoardKey);
          const j = next.indexOf(tappedKey);
          if (i !== -1 && j !== -1) [next[i], next[j]] = [next[j], next[i]];
          vibra.impact('light');
          setRowOrder((prev) => ({ ...prev, [row]: next }));
          setPickedBoardKey(null);
          return;
        }
        // 2c) Tap an empty slot in the row → move picked to the end.
        vibra.impact('light');
        setRowOrder((prev) => ({
          ...prev,
          [row]: [...current.filter((k) => k !== pickedBoardKey), pickedBoardKey],
        }));
        setPickedBoardKey(null);
        return;
      }
      // Different row → MOVE the picked card to that row (capacity permitting).
      // This re-assigns its row this hand (changes the hand — allowed, it's
      // not yet submitted). If the row is full, keep it picked (no-op).
      const capacities = { front: 3, middle: 5, back: 5 } as const;
      if (myDisplayBoard[row].length < capacities[row]) {
        vibra.impact('light');
        placement.moveTo(picked.card, row);
        setPickedBoardKey(null);
      }
      return;
    }

    // 3) Nothing selected → tap a card placed this hand to pick it up.
    if (tappedPending && tappedKey) {
      vibra.impact('light');
      setPickedBoardKey(tappedKey);
    }
  };

  // ── Il trascinamento di una carta della mano (I-798, fase B) ──────────────────────────────────────────────────────────────────────────────────────────
  // Si AGGIUNGE al tocco-tocco: prendere la carta e sceglierla (lo stesso `select` del tocco: si illuminano le file dove puo andare), lasciarla su una fila legale e piazzarla
  // (`piazzaSuFila`), lasciarla altrove e rimettere la selezione com'era prima del gesto — nessuna mossa. Chi gia aveva scelto una carta col tocco la ritrova.
  const trascinaMano: TrascinaMano = {
    onInizio: (card) => {
      const r = remainingDraw.find((x) => cardKey(x.card) === cardKey(card));
      if (!r) return;
      selezionePrima.current = placement.selectedIndex;
      if (placement.selectedIndex !== r.idx) {
        vibra.impact('light');
        setPickedBoardKey(null);
        placement.select(r.idx);
      }
    },
    onSopra: setFilaSopra,
    onRilascio: (card, fila) => {
      const r = remainingDraw.find((x) => cardKey(x.card) === cardKey(card));
      if (!r) return;
      if (fila !== null && highlightRows.includes(fila)) {
        piazzaSuFila(fila);
        return;
      }
      // fuori da una fila legale: com'era (`select` e un interruttore sulla stessa carta, e cambia carta su un'altra)
      const prima = selezionePrima.current;
      if (prima !== r.idx) placement.select(prima ?? r.idx);
    },
  };

  /** Resta solo da scartare: le carte posate bastano (Pineapple: 2 su 3; Fantasyland: le 13) e quello che e ancora in mano e lo scarto. */
  const restaDaScartare = placement.discard !== null || (myFl && placement.isComplete);

  const highlightRows =
    showPlaceUI && placement.selectedIndex !== null
      ? (['front', 'middle', 'back'] as const).filter((row) => {
          // Show rows that are not yet full as legal targets (counting
          // the optimistic pending cards already shown on the board).
          const capacities = { front: 3, middle: 5, back: 5 } as const;
          return myDisplayBoard[row].length < capacities[row];
        })
      : [];

  /** I pezzi del tavolo nel modo dato: le carte hanno taglie diverse in piedi e nel palco. */
  const disegna = (m_: ModoTavolo, cambiaVista: (() => void) | null): PezziTavolo => {
    const palco = m_ === 'palco';
    const altroNettoMio = mySeat ? m.runningNet[mySeat] : 0;
    const altroNettoAvv = oppSeat ? m.runningNet[oppSeat] : 0;
    // Col resoconto aperto la testata dice la mano del RESOCONTO e nessun round: `hand:start` della mano dopo e gia applicato (arriva insieme a `hand:settled`), quindi
    // `m.handNumber` e il round del `state` sono quelli della mano che NON e ancora cominciata per chi guarda («Mano 2 · Round 5/5» accanto a un resoconto della mano 1).
    // I punteggi sono quelli giusti: `runningNet` e gia aggiornato da `hand:settled`.
    const testata = (
      <CpTestata
        modo={m_}
        mano={resoconto ? resoconto.mano : m.handNumber || 1}
        round={resoconto ? null : publicState.round + 1}
        io={altroNettoMio}
        avv={altroNettoAvv}
        onCambiaVista={cambiaVista}
      />
    );

    // Le righe di stato: l'errore del server (si toglie da solo dopo 4 s) e, in Fantasyland, l'invito a sistemare tutto il tavolo.
    const avvisi =
      m.error || (myFl && showPlaceUI) ? (
        <>
          {m.error && <div className="rounded-xl bg-danger/15 px-4 py-2 text-center text-sm font-medium text-danger">{m.error.message}</div>}
          {myFl && showPlaceUI && (
            // in piedi una riga sola e bassa (budget di altezza: la mano della Fantasyland ne ha 20 in meno di quella d'apertura, e l'invito deve starci nel resto); nel palco ha la sua colonna
            <div className={cn('rounded-xl bg-brand/15 text-center font-semibold text-brand', palco ? 'px-3 py-2 text-sm' : 'px-2 py-0.5 text-[12px] leading-4')}>✦ {t('hand.flPlacePrompt')}</div>
          )}
        </>
      ) : undefined;

    if (resoconto) {
      return {
        testata,
        avvisi,
        avversario: null,
        fascia: null,
        mio: null,
        mano: null,
        azioni: null,
        resoconto: <CpResoconto modo={m_} r={resoconto} quattroColori={imp.quattroColori} onProssima={m.prossimaMano} ultima={Boolean(m.matchResult)} secondi={secondiAllaProssima} />,
        pannello: palco && impostazioniAperte ? impostazioni(true) : undefined,
      };
    }

    const avversario = (
      <div data-plancia-avv className="flex flex-col gap-0.5">
        <CpEtichettaPlancia
          nome={t('match.opponent')}
          dettaglio={
            oppFl ? (
              <span className="rounded-md bg-warning/15 px-2 py-0.5 text-[11px] font-semibold text-warning">
                ✦ {t('hand.opponentInFantasyland', { count: oppSeat ? m.flCardCount[oppSeat] : 0 })}
              </span>
            ) : (
              oppCounts !== undefined && (
                <>
                  {t('match.draws', { n: oppCounts.drawCount })} · {t('match.discards', { n: oppCounts.discardCount })}
                </>
              )
            )
          }
        />
        {/* Opponent board: read-only (no onSlotTap passed), placed cards only (server masks the opponent's private state). */}
        <OfcBoard board={oppSeat ? publicState.boards[oppSeat] : emptyBoard} compact taglia={palco ? 'avv' : 'mini'} quattroColori={imp.quattroColori} />
      </div>
    );

    const mio = (
      <div data-plancia-mia className={cn('rounded-2xl transition-all', showPlaceUI ? 'ring-2 ring-brand shadow-[0_0_18px_1px_color-mix(in_srgb,var(--color-brand)_35%,transparent)]' : '')}>
        <OfcBoard
          board={myDisplayBoard}
          taglia={palco ? 'mia-palco' : 'mia'}
          quattroColori={imp.quattroColori}
          filoSulleLibere={showPlaceUI}
          pendingKeys={placedKeys}
          pickedKey={pickedBoardKey ?? undefined}
          onSlotTap={showPlaceUI ? onSlotTap : undefined}
          highlightRows={highlightRows}
          filaSopra={filaSopra}
        />
      </div>
    );

    const fascia = (
      <CpFascia
        modo={m_}
        mioTempo={formatClock(mySeat ? displayClocks[mySeat] : 0)}
        avvTempo={formatClock(oppSeat ? displayClocks[oppSeat] : 0)}
        miaMossa={isMyTurn}
        connesso={m.connected}
        conTurno={palco}
      />
    );
    // In piedi la pastiglia del turno sta SOTTO la mia plancia (I-807); nel palco resta nella fascia.
    const turno = palco ? undefined : <CpTurno modo={m_} miaMossa={isMyTurn} connesso={m.connected} />;

    // Draw tray + placement UI (only when it's my turn, phase=placing)
    const mano = showPlaceUI ? (
      <DrawTray
        cards={remainingDraw.map((r) => r.card)}
        ordine={ordine}
        taglia={palco ? 'mano-palco' : 'mano'}
        trascina={trascinaMano}
        fantasyland={myFl}
        conEtichetta={palco && !manoLarga(palco)}
        larga={manoLarga(palco)}
        quattroColori={imp.quattroColori}
        selectedCard={placement.selectedIndex === null ? null : (m.draw[placement.selectedIndex] ?? null)}
        discarding={restaDaScartare}
        onSelect={(card) => {
          // La carta, mai la sua posizione a schermo: con «Ordina» l'ordine visivo non e quello di `m.draw`, e `placement.select` vuole l'indice di `m.draw`.
          const r = remainingDraw.find((x) => cardKey(x.card) === cardKey(card));
          if (!r) return;
          vibra.impact('light');
          setPickedBoardKey(null);
          placement.select(r.idx);
        }}
      />
    ) : (
      /* Waiting for opponent or non-placing phase; dopo «Prossima mano» il seed e partito e si aspetta la distribuzione (l'avversario deve mandare il suo) */
      ((!isMyTurn && isPlacingPhase) || m.attesaDistribuzione) && (
        <div className="flex items-center justify-center py-3">
          <span className="tav-vetro rounded-xl px-4 py-2 text-sm font-semibold text-fg-muted">{t('match.waitingForOpponent')}</span>
        </div>
      )
    );

    const azioni = (
      <CpAzioni
        modo={m_}
        ordina={showPlaceUI && remainingDraw.length >= 2 ? <TastoOrdina ordine={ordine} etichetta={t(`match.ordina.${ordine}`)} onClick={() => setOrdine(altroOrdine)} /> : undefined}
        puoGiocare={showPlaceUI}
        confermabile={placement.isComplete}
        evidenzia={restaDaScartare}
        onConferma={handleConfirm}
        onAbbandona={() => setConfermaAbbandono(true)}
        onImpostazioni={() => setImpostazioniAperte(true)}
        abbandonabile={!m.matchResult}
      />
    );

    return { testata, avvisi, avversario, fascia, mio, turno, mano, azioni, fissaInFondo: true, manoLarga: showPlaceUI && manoLarga(palco), confermaPalco: palco ? <CpConfermaAbbandono palco open={confermaAbbandono} onAnnulla={() => setConfermaAbbandono(false)} onAbbandona={abbandona} /> : undefined, pannello: palco && impostazioniAperte ? impostazioni(true) : undefined };
  };

  /** Il contenuto delle impostazioni (nel palco e il pannello a destra; in piedi lo stesso contenuto sta nel foglio). */
  const impostazioni = (palco: boolean) => (
    <ImpostazioniContenuto
      palco={palco}
      quattroColori={imp.quattroColori}
      onQuattroColori={imp.impostaQuattroColori}
      vibrazione={imp.vibrazione}
      onVibrazione={imp.impostaVibrazione}
      onClose={() => setImpostazioniAperte(false)}
    />
  );

  return (
    // ⚠️ Niente `padding-top` dell'inset: la tacca sta GIA in `#root` (`globals.css`), e contarla qui la contava due volte (+47 px sul telefono di riferimento, misurato dal banco).
    <div className="relative mx-auto flex max-w-[520px] flex-col gap-3 px-3 pb-1 pt-1">
      <CpTavoloResponsivo disegna={disegna} onModo={setModo} />
      {modo === 'verticale' && <CpConfermaAbbandono open={confermaAbbandono} onAnnulla={() => setConfermaAbbandono(false)} onAbbandona={abbandona} />}
      {modo === 'verticale' && (
        <CpImpostazioniFoglio
          open={impostazioniAperte}
          onClose={() => setImpostazioniAperte(false)}
          quattroColori={imp.quattroColori}
          onQuattroColori={imp.impostaQuattroColori}
          vibrazione={imp.vibrazione}
          onVibrazione={imp.impostaVibrazione}
        />
      )}
    </div>
  );
}

/** Quante carte ha ogni mia fila (le del server e quelle posate in questa mano e non ancora confermate). */
function myFileConteggio(board: { front: Card[]; middle: Card[]; back: Card[] }, pending: { row: Row }[]) {
  const n = (row: Row) => board[row].length + pending.filter((p) => p.row === row).length;
  return { front: n('front'), middle: n('middle'), back: n('back') };
}
